import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { JobsService } from 'src/app/modules/incidents/services/jobs/jobs.service';
import { JobDetailsItemViewModel } from 'src/app/modules/incidents/viewModels/jobs/jobDetailsItemViewModel';
import { AreaViewModel } from 'src/app/modules/settings/viewModels/areaViewModel';
import { EditableFieldTypes } from 'src/app/modules/shared/enums/editableFieldTypes';
import { FilterDateOptions } from 'src/app/modules/shared/enums/filter/filterDateOptions';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { JobPriorities } from 'src/app/modules/shared/enums/jobs/jobpriorities';
import { JobStatuses } from 'src/app/modules/shared/enums/jobs/jobStatuses';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { PrivacyStatuses } from 'src/app/modules/shared/enums/privacyStatuses';
import { Account } from 'src/app/modules/shared/models/account';
import { Employee } from 'src/app/modules/shared/models/employee';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { LocationService } from 'src/app/modules/shared/services/location.service';
import { FilterToViewModelUtilityService } from 'src/app/modules/shared/services/utilities/filterToViewModelUtilityService';
import { FilterUtilities } from 'src/app/modules/shared/utilities/filter.utilities';
import { LocationUtilities } from 'src/app/modules/shared/utilities/location.utilities';
import { LocationViewModel } from 'src/app/modules/shared/viewModels/locationViewModel';
import { ZoneViewModel } from 'src/app/modules/shared/viewModels/zoneViewModel';
import { T } from 'src/assets/i18n/translation-keys';
import { EnumUtilities } from 'src/app/modules/shared/utilities/enum.utilities';
import { FixedFilters } from 'src/app/modules/shared/enums/fixedFilters.enum';
import { CachingService } from 'src/app/modules/shared/services/caching.service';
import { FixedZoneFiltersService } from 'src/app/modules/shared/services/fixed-zone-filters.service';
import { JobPrivacyStatusViewModel } from 'src/app/modules/incidents/viewModels/jobs/jobPrivacyStatusViewModel';
import { JobStatusViewModel } from 'src/app/modules/incidents/viewModels/jobs/jobStatusViewModel';
import { JobCategoriesService } from 'src/app/modules/settings/services/jobs/job-categories.service';
import { JobCategoryViewModel } from 'src/app/modules/settings/viewModels/jobs/jobCategoryViewModel';
import { Constants } from 'src/app/modules/shared/models/constants';
import { ManageExternalUserComponent } from 'src/app/modules/settings/components/common/modals/employee-permissions/manage-external-user/manage-external-user.component';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';


@Component({
    selector: 'app-job-add-modal',
    templateUrl: './job-add-modal.component.html',
    styleUrls: ['./job-add-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [JobCategoriesService],
    standalone: false
})
export class JobAddModalComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter();

  @ViewChild('job_add_modal_dropdown') jobAddModalDropdown: ElementRef;

  // added for PIR linking
  public itemCreated: Subject<{item: JobDetailsItemViewModel, objectType: ObjectTypes}> = new Subject();

  public currentSelectedPriorityFilter: FilterViewModel;
  public job: JobDetailsItemViewModel = new JobDetailsItemViewModel();
  public privacyStatuses: JobPrivacyStatusViewModel[] = EnumUtilities.items(PrivacyStatuses);
  public privacyStatus: JobPrivacyStatusViewModel;
  public jobStatuses: JobStatusViewModel[] = EnumUtilities.items(JobStatuses);
  public jobStatus: JobStatusViewModel;
  public triggerIndicatorErrors: boolean = false;
  public validationErrors: string[];
  public filters: FilterViewModel[] = [];
  public jobForm: UntypedFormGroup;
  public loading: boolean = false;
  public jobStartTime: string = '';
  public jobEndTime: string = '';
  public quickAddTitle: string = '';
  public setDefaultOwner: boolean = true;
  public setDefaultDepartment: boolean = true;
  public currentAccount: Account;
  public currentEmployee: Employee;
  public localisedJob: string;
  public localisedJobs: string;
  public localisedZone: string;
  public localisedArea: string;
  public filterTypes = FilterTypes;
  public objectTypes = ObjectTypes;
  public editableFieldTypes = EditableFieldTypes;
  public objectType: ObjectTypes = ObjectTypes.Job;
  public priorityTitles: string[] = [];
  public zonesViewModels: ZoneViewModel[] = [];
  public areasViewModels: AreaViewModel[] = [];
  public mapZones: ZoneViewModel[] = [];
  public mapAreas: AreaViewModel[] = [];
  public selectedZone: FilterViewModel;
  public areaFilters: FilterViewModel[] = [];
  public zoneFilters: FilterViewModel[] = [];
  public applicableAreaFilters: FilterViewModel[] = [];
  public initialSelectedZone: FilterViewModel;
  public navigateToDetailsPage: boolean = true;
  public isWithinEventDetails: boolean = false;
  // Preselected PIR fields & event association
  public isCalledFromPIR: boolean = false;
  protected copyEventFromPIR: boolean;
  public copiedEventFilter: FilterViewModel;
  public preselectedLocation: LocationViewModel = null;
  public preselectedPIRPriorityValue: number;
  public preselectedEventId: number;

  public symbols: Set<string> = new Set();
  private zones: ZoneViewModel[] = [];
  private lastAppliedCategoryId: string = null;

  public readonly T = T;
  private subscriptions: Subscription = new Subscription();

  public localisedDepartment = 'Department';
  public localisedDepartments = 'Departments';
  public localisedPrivacyStatuses: JobPrivacyStatusViewModel[];
  public selectedJobCategory: FilterViewModel;
  public selectedJobType: FilterViewModel;

  public isMobile: boolean = false;
  public mobileWidth = Constants.xs;
  jobCategories: JobCategoryViewModel[] = [];
  public invalidDescription: boolean = false;
  public riskId: number;
  public currentlySelectedExternalUser: FilterViewModel;

  constructor(
    public readonly bsModalRef: BsModalRef,
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly alertService: AlertService,
    private readonly jobsService: JobsService,
    private readonly locationService: LocationService,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localisationService: LocalisationService,
    private readonly authenticationService: AuthenticationService,
    private readonly filterToViewModelUtilityService: FilterToViewModelUtilityService,
    private readonly translateService: TranslateService,
    private readonly fixedZoneService: FixedZoneFiltersService,
    private readonly cachingService: CachingService,
    private readonly wtValidators: WtValidators,
    private readonly jobCategoriesService: JobCategoriesService,
    private readonly modalService: BsModalService,
  ) {}

  ngOnInit() {
    this.initJobForm();
    this.closed.emit();
    this.getJobCategories();
    this.currentEmployee = this.authenticationService.getCurrentEmployee();
    this.currentAccount = this.authenticationService.getCurrentAccount();
    this.jobStatus = this.jobStatuses.find((x) => x.key === JobStatuses.New);
    this.isMobile = window.innerWidth <= this.mobileWidth;

    this.localisedPrivacyStatuses = this.localisationService.getLocalizedIncidentPrivacyStatuses();

    this.privacyStatus = this.localisedPrivacyStatuses.find((x) => x.key === PrivacyStatuses.Open);

    this.copyEventFromPIR = this.isCalledFromPIR;

    if(this.preselectedEventId) {
      const preselectedEventFilter = FilterUtilities.GenerateFilter(FilterTypes.Event, this.preselectedEventId);
      this.filters.push(preselectedEventFilter);
    }

    if(this.isCalledFromPIR && this.copiedEventFilter) {
      this.filters.push(this.copiedEventFilter);
      this.job.eventId = +this.copiedEventFilter.filterValue;
    }

    this.setDefaultStartEndTimes();
    this.getZones();
    this.initAreaZonesFilters();

    if(this.filters && this.filters.length) {
      const jobTypeFilter = this.filters.find(f => f.filterType === FilterTypes.Job_Type);

      if(!jobTypeFilter) {
        this.initDefaultTypeFilter();
      } else {
        this.selectedJobType = jobTypeFilter;
        this.selectedJobCategory = this.filters.find(f => f.filterType === FilterTypes.Job_Category);
        if(this.selectedJobCategory) {
          if(!this.jobCategories || !this.jobCategories.length) {
            this.getJobCategory(this.selectedJobCategory.filterValue);
          } else {
            const category = this.jobCategories.find(c => c.id === +this.selectedJobCategory.filterValue);

            if(category) {
              this.populateJobDetailsFromCategory(category);
            }
          }
        }
      }
    } else {
      this.initDefaultTypeFilter();
    }


    this.initLocalisations();
    this.initPriorityFilterTitles();
    this.initLiveEventZoneFilters();

    if(this.preselectedLocation) {
      this.onJobLocationUpdated(this.preselectedLocation);
    }

    if (this.setDefaultDepartment) {
      this.addCurrentUserDepartment();
    }

    if (this.setDefaultOwner) {
      this.addCurrentUserAsOwner();
    }

    if(this.quickAddTitle.length) this.jobForm.controls.title.setValue(this.quickAddTitle)

    if(this.isCalledFromPIR) {
      if(!this.preselectedPIRPriorityValue) this.preselectedPIRPriorityValue = 0;

      const preselectedPriorityFilter = FilterUtilities.GenerateFilter(FilterTypes.Job_Priority, this.preselectedPIRPriorityValue);

      this.onApply(preselectedPriorityFilter);
    }

  }

  private populateJobDetailsFromCategory(category: JobCategoryViewModel) {
    this.job.description = category.description;
    this.jobForm.controls.description.setValue(category.description);
    this.job.privacyStatus = category.privacyStatus;
    this.privacyStatus = this.localisedPrivacyStatuses.find(x => x.key === category.privacyStatus);
    const categoryRelatedFilters = category.filters.filter(f => f.filterType === FilterTypes.Owner || f.filterType === FilterTypes.Subscriber
      || f.filterType === FilterTypes.Department || f.filterType === FilterTypes.Zone || f.filterType === FilterTypes.Tag || f.filterType === FilterTypes.User_Group || f.filterType === FilterTypes.External_User);
    this.job.filters.push(FilterUtilities.GenerateFilter(FilterTypes.Privacy_Status, category.privacyStatus, ''));
    if (categoryRelatedFilters && categoryRelatedFilters.length) {
      this.job.filters.push(...categoryRelatedFilters);
      this.filters.push(...categoryRelatedFilters);
      this.filters = this.removeDuplicates(this.filters);
      if (this.filters.some(f => f.filterType === FilterTypes.External_User)) {
        this.setExternalUserFilter(this.filters?.find(x => x.filterType === FilterTypes.External_User));
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getJobCategories() {
    this.jobCategoriesService.list().subscribe(res => {
      if(res) {
        this.jobCategories = res;
        this.jobCategories.forEach((category) => {
          category.filters = this.removeDuplicates(category.filters);
        });
      }
    })
  }

  getJobCategory(categoryId: string) {
    this.loading = true;
    this.jobCategoriesService.details(+categoryId).subscribe(res => {
      if(res) {
        this.job.privacyStatus = res.privacyStatus;
        this.privacyStatus = this.localisedPrivacyStatuses.find(x => x.key === res.privacyStatus);
        if(!this.job.filters)
          this.job.filters = [];

        this.populateJobDetailsFromCategory(res);

        this.loading = false;
        this.changeDetectorRef.markForCheck();
      }
    })
  }

  get position(): {lat: number, lng: number} {
    return {
      lat: this.job.latitude,
      lng: this.job.longitude
    }
  }

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  public onJobStatusSelected(jobStatus: { key: JobStatuses; value: string }): void {
    this.jobStatus = jobStatus;
    this.job.status = jobStatus.key;
  }

  public onPrivacyStatusSelected(privacyStatus: { key: PrivacyStatuses; value: string }) {
    this.privacyStatus = privacyStatus;
    this.job.privacyStatus = privacyStatus.key;
  }

  public addNewExternalUser(): void {
    let modalConfig: ModalOptions<ManageExternalUserComponent> = {
      backdrop: 'static',
      keyboard: false,
      class: 'modal-right-end-full-height-add',
    };
    const initialState = {
      editMode: false,
      employeeDetailsViewModel: null,
      useOptions: true,
      currentlySelectedExternalUserId: this.currentlySelectedExternalUser?.filterValue
    };

    const modalParams = Object.assign({}, modalConfig, { initialState });
    const bsModalRef = this.modalService.show(ManageExternalUserComponent, modalParams);
    bsModalRef.content.onAddExistingUser.subscribe((filters: FilterViewModel[]) => {
      const filtersWithoutExternalUsers = this.filters.filter(f => f.filterType !== FilterTypes.External_User);
      const mergedFilters = [...filtersWithoutExternalUsers, ...filters];
      this.currentlySelectedExternalUser = filters.length > 0 ? filters[0] : null;
      this.handleFilterChange(mergedFilters, FilterTypes.External_User);
      this.changeDetectorRef.markForCheck();
    });
    bsModalRef.content.onCreateAndAddExternalUser.subscribe((data: EmployeeDetailsViewModel) => {
      const externalUserFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.External_User);
      const newExternalUserFilter = externalUserFilters.find(f => f.filterValue === data.id);
      const filtersWithoutExternalUsers = this.filters.filter(f => f.filterType !== FilterTypes.External_User);
      const mergedFilters = [...filtersWithoutExternalUsers, newExternalUserFilter];
      this.currentlySelectedExternalUser = newExternalUserFilter;
      this.handleFilterChange(mergedFilters, FilterTypes.External_User);
      this.changeDetectorRef.markForCheck();
    });
  }

  public handleFilterChange(filters: FilterViewModel[], filterType: FilterTypes = null) {
    this.filters = filters.slice();
    this.selectedZone = filters.find((f) => f.filterType === FilterTypes.Zone);
    this.selectedJobCategory = filters.find((f) => f.filterType === FilterTypes.Job_Category);
    if (this.selectedJobCategory) {
      const category = this.jobCategories.find((c) => c.id === +this.selectedJobCategory.filterValue);

      if (!category) {
        return;
      }

      category.filters = this.removeDuplicates(category.filters);
      this.job.description = category.description;
      const allFilters = this.allowedFiltersService.getCachedAllAllowedFilters();

      if (this.lastAppliedCategoryId !== this.selectedJobCategory.filterValue) {
        this.setCategoryData(category, allFilters, FilterTypes.Owner);
        this.setCategoryData(category, allFilters, FilterTypes.Department);
        this.setCategoryData(category, allFilters, FilterTypes.User_Group);
        this.setCategoryData(category, allFilters, FilterTypes.Zone);
        this.setCategoryData(category, allFilters, FilterTypes.Privacy_Status);
        this.setCategoryData(category, allFilters, FilterTypes.External_User);

        this.privacyStatus = this.localisedPrivacyStatuses.find(x => x.key === category.privacyStatus);
        this.job.privacyStatus = category.privacyStatus;
        this.lastAppliedCategoryId = this.selectedJobCategory.filterValue;
      }
    }

    if (this.filters.some(f => f.filterType === FilterTypes.External_User)) {
      this.setExternalUserFilter(this.filters?.find(x => x.filterType === FilterTypes.External_User));
    } else if (filterType === FilterTypes.External_User) {
      this.currentlySelectedExternalUser = null;
    }

    this.setZonesAndAreas();

    this.changeDetectorRef.detectChanges();
  }

  private setExternalUserFilter(filter: FilterViewModel) {
    this.currentlySelectedExternalUser = filter;
  }

  private removeDuplicates(filters: FilterViewModel[]): FilterViewModel[] {
    return filters.filter((obj1, i, arr) => arr.findIndex(obj2 => (obj2.id === obj1.id)) === i);
  }

  private setCategoryData(category: JobCategoryViewModel, allFilters: FilterViewModel[], filterType: FilterTypes) {
    const dataIds = category.filters.filter((f) => f.filterType === filterType).map(f => f.filterValue);
    const data = allFilters.filter(f => f.filterType === filterType && dataIds.includes(f.filterValue));
    this.filters = this.filters.slice();
    this.filters = this.filters.filter(f => f.filterType !== filterType);
    this.filters.push(...data);
    this.filters = this.removeDuplicates(this.filters);
  }

  public handleJobTypeChange(filters: FilterViewModel[]) {
    this.filters = filters.slice();
    this.selectedJobType = filters.find((f) => f.filterType === FilterTypes.Job_Type);

    this.changeDetectorRef.detectChanges();
  }

  public onDescriptionChange(text: string): void {
    this.jobForm.controls.description.setValue(text);
  }

  public localiseString(s: string): string {
    return this.localisationService.localise(s);
  }

  public clearPriority(): void {
    this.jobForm.controls.priority.setValue(0);
  }

  public addCurrentUserDepartment() {
    const filters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Department);

    if (filters) {
      const departmentFilter = filters.find(
        (f) => f.filterType === FilterTypes.Department && f.filterValue === this.currentEmployee.departmentId
      );
      if (
        departmentFilter &&
        !this.filters.some((f) => f.filterType === FilterTypes.Department && f.filterValue === this.currentEmployee.departmentId)
      ) {
        this.filters = this.filters.slice();
        this.filters.push(departmentFilter);
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  public addCurrentUserAsOwner(): void {
    const filters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Owner);

    if (filters) {
      const empFilter = filters.find(
        s => s.filterType === FilterTypes.Owner && s.filterValue.toString() === this.currentEmployee.id.toString()
      );
      if (empFilter && !this.filters.some((f) => f.filterType === FilterTypes.Owner && f.filterValue === this.currentEmployee.id)) {
        this.filters = this.filters.slice();
        this.filters.push(empFilter);
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  public onJobLocationUpdated(location: LocationViewModel) {
    this.job.locationDetails = location.locationDetails;
    this.job.latitude = location.latitude;
    this.job.longitude = location.longitude;
  }

  protected validateState(): boolean {
    return (
      !(
        this.jobForm.controls.title.valid &&
        this.jobForm.controls.headlineStatus.valid &&
        this.jobForm.controls.description.valid &&
        !this.invalidDescription
      ) || !this.filters.some((x) => x.filterType === FilterTypes.Job_Type)
        || !this.filters.some((x) => x.filterType === FilterTypes.Job_Category)
    );
  }

  private initLiveEventZoneFilters() {
    const preselectedEventFilter = this.filters.find(f => f.filterType === FilterTypes.Event);
    if(preselectedEventFilter) this.job.eventId = +preselectedEventFilter.filterValue;

    if (this.currentAccount.fixedFilters === FixedFilters.Event && !preselectedEventFilter) {
      const eventID = Number(this.cachingService.selectedEventID) || 0;
      this.job.eventId = eventID;
      this.filters.push(FilterUtilities.GenerateFilter(FilterTypes.Event, this.job.eventId.toString(), ''));
    } else if (this.currentAccount.fixedFilters === FixedFilters.VenueZone && !preselectedEventFilter) {
      const zoneId = +this.cachingService.selectedZoneID;
      if (zoneId > 0) {
        this.initialSelectedZone = this.fixedZoneService.fixedFilter;
        this.filters.push(this.initialSelectedZone);
      }
    }
  }

  private setZonesAndAreas() {
    this.mapZones = this.filterToViewModelUtilityService.getZoneViewModelsFromFilters(this.filters, this.zones);
    this.mapAreas = this.filterToViewModelUtilityService.getAreaViewModelsFromFilters(this.filters);

    this.setApplicableFilters();
    this.changeDetectorRef.markForCheck();
  }

  public onSave(navigateToDetailsPage: boolean = false): void {
    this.loading = true;

    if(this.isCalledFromPIR && !this.copyEventFromPIR && this.copiedEventFilter) {
      const filtersWithoutEventFilter = this.filters.filter(f => f.filterType !== this.filterTypes.Event);
      this.filters = filtersWithoutEventFilter;
      this.job.eventId = null;
    }

    this.job.filters = this.filters;
    this.job.jobTypeId = this.filters.find((x) => x.filterType === FilterTypes.Job_Type).filterValue;
    this.job.filters.push(FilterUtilities.GenerateFilter(FilterTypes.Date, this.jobStartTime, '', FilterDateOptions.Start_Date));
    this.job.filters.push(FilterUtilities.GenerateFilter(FilterTypes.Date, this.jobEndTime, '', FilterDateOptions.Due_Date));
    this.job.start = this.jobStartTime;
    this.job.due = this.jobEndTime;

    const item: JobDetailsItemViewModel = { ...this.job, ...this.jobForm.value };
    const addMethod = this.riskId ? this.jobsService.addForRisk(item, this.riskId) : this.jobsService.add(item);
    addMethod.subscribe((validateModel) => {
      const errors = validateModel.errorList;

      if (errors?.length > 0) {
        this.validationErrors = errors;
        this.loading = false;
      } else {
        if (navigateToDetailsPage) {
          void this.router.navigate([`${this.currentAccount.accountUID}/control/jobs/` + String(validateModel.returnModel.id)]);
          this.closeModal();
        } else {
          this.itemCreated.next({item: validateModel.returnModel as JobDetailsItemViewModel, objectType: this.objectType});
          this.closeModal();
        }

        this.loading = false;
        if (this.isWithinEventDetails) return;
        void this.alertService.success(this.translateService.instant(T.common.item_was_added_successfully, { item: this.localisedJob }));
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private setDefaultStartEndTimes(): void {
    this.jobStartTime = moment().set({ hour: 7, minute: 0, second: 0, millisecond: 0 }).toISOString();
    this.jobEndTime = moment().set({ hour: 18, minute: 0, second: 0, millisecond: 0 }).toISOString();
  }

  private initJobForm(): void {
    this.jobForm = this.fb.group({
      title: [
        '',
        {
          validators: [
            Validators.required,
            this.wtValidators.textInput(3, 250, WtValidators.RESTRICTED_CHARS.DEFAULT),
          ],
        },
      ],
      category: [
        '',
        {
          validators: [
            Validators.required
          ],
        },
      ],
      headlineStatus: [
        '',
        { validators: [Validators.minLength(3), Validators.maxLength(255), this.wtValidators.restrictedChars(WtValidators.RESTRICTED_CHARS.DEFAULT)] },
      ],
      description: ['', { validators: [Validators.minLength(0), this.wtValidators.htmlStrippedMaxLength(Constants.richTextMaxLength)] }],
      startTime: [''],
      endTime: [''],
      priority: [0],
    });
  }

  private initLocalisations(): void {
    this.localisedJob = this.localisationService.localiseObjectType(ObjectTypes.Job);
    this.localisedJobs = this.localisationService.localiseObjectType(ObjectTypes.Job, true);
    this.localisedZone = this.localisationService.localiseObjectType(ObjectTypes.Zone);
    this.localisedArea = this.localisationService.localiseObjectType(ObjectTypes.Area);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);
    this.localisedDepartments = this.localisationService.localiseObjectType(ObjectTypes.Department, true);
  }

  private initPriorityFilterTitles(): void {
    const level_one = this.localisationService.localiseFilterValueByFilterType(JobPriorities.One_Star, FilterTypes.Job_Priority);
    const level_two = this.localisationService.localiseFilterValueByFilterType(JobPriorities.Two_Stars, FilterTypes.Job_Priority);
    const level_three = this.localisationService.localiseFilterValueByFilterType(
      JobPriorities.Three_Stars,
      FilterTypes.Job_Priority
    );

    this.priorityTitles = [level_one, level_two, level_three];
  }

  private initAreaZonesFilters(): void {
    this.areaFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Area);
    this.zoneFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Zone);
  }

  private initDefaultTypeFilter(): void {
    const defaultJobTypeFilter = this.allowedFiltersService
      .getCachedAllAllowedFilters()
      .filter((x) => x.filterType === FilterTypes.Job_Type)[0];
    this.filters.push(defaultJobTypeFilter);
    this.selectedJobType = defaultJobTypeFilter;
  }

  private setApplicableFilters(): void {
    if (this.mapZones && this.mapZones.length) {
      const areas: FilterViewModel[] = [];
      this.mapZones.forEach((zone) => {
        const zoneAreas = this.areaFilters.filter((a) => +a.relatedObjectId === zone.id);

        if (zoneAreas) {
          areas.push(...zoneAreas);
        }
      });

      this.applicableAreaFilters = areas.slice();

      this.filters.forEach((filter) => {
        if (filter.filterType === FilterTypes.Area) {
          if (!this.mapZones.some((zone) => zone.id === filter.relatedObjectId)) {
            this.filters = this.filters.filter((x) => x.filterValue !== filter.filterValue);
          }
        }
      });

      if (!this.job.latitude && !this.job.longitude && this.filters.some((x) => x.filterType === FilterTypes.Zone)) {
        this.setInitialZonePin();
      }
    } else {
      this.mapAreas = [];
      this.applicableAreaFilters = [];
      this.filters = this.filters.filter((x) => x.filterType !== FilterTypes.Area);
    }
  }

  private setInitialZonePin() {
    this.job = LocationUtilities.setLocationDetails(this.mapZones, this.job) as JobDetailsItemViewModel;

    this.changeDetectorRef.detectChanges();
  }

  private getZones(): void {
    this.locationService.getZones().subscribe((res) => {
      this.zones = res;
      this.setZonesAndAreas();

      this.loading = false;
      this.changeDetectorRef.detectChanges();
    });
  }


  public onApply(priorityFilter: FilterViewModel): void {
    this.currentSelectedPriorityFilter = priorityFilter;
    this.jobForm.patchValue({priority: this.currentSelectedPriorityFilter.filterValue})
  }

  protected onStartDateChanged(startDate: any) {
    this.jobStartTime = startDate;
    this.jobForm.controls.startTime.setValue(moment(startDate).format('HH:mm, MM/DD/YYYY'));
  }

  protected onEndDateChanged(endDate: any) {
    this.jobEndTime = endDate;
    this.jobForm.controls.endTime.setValue(moment(endDate).format('HH:mm, MM/DD/YYYY'));
  }

  public isDescriptionInvalid(disable: boolean) {
    this.invalidDescription = disable;
    this.changeDetectorRef.detectChanges();
  }
}
